//@flow
import * as Immutable from 'immutable';
import * as React from 'react';
import {
    HomebaseType,
    MasterHomebase,
    CrossDocksType
} from 'common/models/internal/MasterHomebase';
import { Alpha2CountryNameMap } from 'common/constants/countryCodes';
import {
    entityMasterSingleEditFactory,
    type Field
} from 'common/entity/generic/SingleEntityEditContainer';

import HomebaseActions from 'common/redux/actions/homebaseMaster';
import {
    cities as citiesApi,
    pincodes as pincodesApi,
    homebaseMaster as homebaseApi
} from 'common/middleware/api';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { ClientSelectors } from 'common/redux/selectors/clientSelector';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import { ENTITY_NAME_ID } from 'common/constants/entity/homebaseMaster';
import ResourceLimitInput from 'common/entity/components/resourceLimit/ResourceLimitInput';
import { ResourceLimit } from 'common/models/internal/ResourceLimit';

const HomebaseTypeOptions = HomebaseType.getOptions({
    translationKey: 'masterHomebaseProperties.types'
});

const CountryCodeOptions = Object.keys(Alpha2CountryNameMap).map(
    countryCode => {
        const countryName = Alpha2CountryNameMap[countryCode];
        return { value: countryCode, label: countryName };
    }
);

const formatCreateLabel = t => label => {
    return t('homebaseForm.createPrompt', {
        code: label
    });
};

const HOMEBASE_FIELDS: Array<Field> = [
    {
        accessor: 'code',
        label: 'homebaseForm.code',
        helpText: 'homebaseForm.codeTooltip',
        placeholder: 'homebaseForm.codePlaceholder',
        disabledOnEdit: true,
        required: true,
        halfWidth: false,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        extraProps: ({ entity, field, props, t }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.getIn(['id']),
                code: entity.getIn(field.absoluteAccessor),
                conflictEntities: t('entityManagement:entityMasterTitle', {
                    entityNameId: 'masterLocation'
                }),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            }
            if ('code' in value) {
                return entity.setCode(code);
            }
        }
    },
    {
        accessor: 'name',
        label: 'homebaseForm.name',
        helpText: 'homebaseForm.nameTooltip',
        placeholder: 'homebaseForm.namePlaceholder',
        required: true,
        halfWidth: false,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'type',
        label: 'homebaseForm.type',
        type: FieldTypeEnum.Select,
        required: true,
        placeholder: 'homebaseForm.typePlaceholder',
        options: HomebaseTypeOptions,
        dependentAccessors: ['crossDockInfo'],
        onChangeHandler: ({
            field,
            entity,
            value
        }: {
            entity: MasterHomebase
        }) => {
            let updatedEntity = entity;
            const { crossDockInfo } = updatedEntity;
            if (value.value === HomebaseType.Homebase && crossDockInfo) {
                const updatedCrossDockInfo = crossDockInfo
                    .setTaskLimit(null)
                    .setTypesSupported(Immutable.List());
                updatedEntity = updatedEntity.setCrossDockInfo(
                    updatedCrossDockInfo
                );
            }
            return updatedEntity.setIn(field.absoluteAccessor, value.value);
        }
    },
    {
        accessor: 'crossDockInfo',
        label: 'homebaseForm.crossDockType',
        placeholder: 'homebaseForm.crossDockTypePlaceholder',
        type: FieldTypeEnum.Select,
        extraProps: ({ entity: { type }, field }) => {
            return {
                isMulti: true,
                disabled: type !== HomebaseType.Crossdock,
                value: field.value.typesSupported.isEmpty()
                    ? null
                    : field.value.typesSupported.toArray()
            };
        },
        options: CrossDocksType.getOptions({
            translationKey: 'masterHomebaseProperties.crossDockTypes'
        }),
        onChangeHandler: ({
            entity,
            field,
            value
        }: {
            entity: MasterHomebase,
            value: Array<$FixMe>
        }) => {
            let updatedEntity = entity;
            const { crossDockInfo } = updatedEntity;
            const crossDockValues = value.map(({ value }) => value);
            if (
                crossDockInfo &&
                !updatedEntity.crossDockInfo?.taskLimit &&
                crossDockValues.includes(CrossDocksType.VehicleToVehicle)
            ) {
                const updatedCrossDockInfo = crossDockInfo.setTaskLimit(
                    ResourceLimit.defaultInit()
                );
                updatedEntity = updatedEntity.setCrossDockInfo(
                    updatedCrossDockInfo
                );
            }
            return updatedEntity.setIn(
                [...field.absoluteAccessor, 'typesSupported'],
                Immutable.List(crossDockValues)
            );
        }
    },
    {
        accessor: 'crossDockInfo.taskLimit',
        component: ({ shouldRender, ...props }) => {
            return shouldRender ? <ResourceLimitInput {...props} /> : null;
        },
        onChangeHandler: ({ entity, value }: { entity: MasterHomebase }) => {
            const { crossDockInfo } = entity;
            const { higherThresh, lowerThresh } = value;
            if (crossDockInfo) {
                const { taskLimit } = crossDockInfo;
                let updatedTaskLimit = taskLimit || ResourceLimit.defaultInit();
                updatedTaskLimit = updatedTaskLimit
                    .setHigherThresh(higherThresh)
                    .setLowerThresh(lowerThresh)
                    .setAllValidationErrors();

                const updatedCrossDockInfo = crossDockInfo.setTaskLimit(
                    updatedTaskLimit
                );
                return entity.setCrossDockInfo(updatedCrossDockInfo);
            }

            return entity;
        },
        extraProps: ({ entity, t }: { entity: MasterHomebase }) => {
            const name = 'task';
            const { crossDockInfo } = entity;
            const resourceLimit = crossDockInfo?.taskLimit
                ? crossDockInfo.taskLimit
                : ResourceLimit.defaultInit();

            return {
                shouldRender: entity.crossDockInfo?.typesSupported.includes(
                    CrossDocksType.VehicleToVehicle
                ),
                name,
                resourceName: name,
                resourceLimit,
                label: t(`masterRiderPersonaProperties.${name}`),
                lowerThreshPlaceHolder: t(
                    'homebaseForm.minTaskLimitPlaceholder'
                ),
                higherThreshPlaceHolder: t(
                    'homebaseForm.maxTaskLimitPlaceholder'
                )
            };
        }
    },
    {
        accessor: 'teams',
        label: 'homebaseForm.teams',
        dependentAccessors: ['contact'],
        helpText: 'homebaseForm.teamsTooltip',
        type: FieldTypeEnum.Teams,
        required: true
    },
    {
        accessor: 'contact',
        type: FieldTypeEnum.Contact,
        extraProps: ({ entity, t, props }) => {
            const { validateDuplicateId } = props;
            return {
                validateDuplicateId,
                translationsObject: {
                    label: t('homebaseForm.contact'),
                    helpText: t('homebaseForm.contactTooltip'),
                    addContactText: t('homebaseForm.addContact'),
                    overrideMessage: t('homebaseForm.addContactOverride')
                },
                teamIds: entity.getIn(['teams'])
            };
        },
        onChangeHandler: ({ entity, value }) => {
            return entity.setContactAndContactId(value);
        }
    },
    {
        accessor: 'address.placeName',
        dependentAccessors: ['latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        label: 'homebaseForm.placeName',
        helpText: 'homebaseForm.placeNameTooltip',
        placeholder: 'homebaseForm.placeNamePlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.formattedAddress',
        dependentAccessors: ['latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        label: 'homebaseForm.address',
        required: true,
        placeholder: 'homebaseForm.addressPlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.localityName',
        dependentAccessors: ['latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        label: 'homebaseForm.locality',
        placeholder: 'homebaseForm.localityPlaceholder',
        halfWidth: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.city',
        label: 'homebaseForm.city',
        placeholder: 'homebaseForm.cityPlaceholder',
        dependentAccessors: ['latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        type: FieldTypeEnum.City,
        halfWidth: true,
        options: ({ field, props }) => {
            return {
                data: field.fetchInfo.response,
                countryCode: props.defaultCountryCode,
                isLoading: !field.fetchInfo.response
            };
        },
        extraProps: ({ t }) => {
            return {
                formatCreateLabel: formatCreateLabel(t)
            };
        },
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value?.value),
        fetchInfo: {
            fetcher: citiesApi.fetch.bind(citiesApi),
            params: ({ entity, props }) => {
                const countryCode = entity.getIn(['address', 'countryCode']);
                return { countryId: countryCode || props.defaultCountryCode };
            },
            response: null
        }
    },
    {
        accessor: 'address.state',
        dependentAccessors: ['latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        label: 'homebaseForm.state',
        placeholder: 'homebaseForm.statePlaceholder',
        halfWidth: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.pincode',
        dependentAccessors: ['latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        label: 'homebaseForm.pincode',
        placeholder: 'homebaseForm.pincodePlaceholder',
        type: FieldTypeEnum.Pincode,
        halfWidth: true,
        options: ({ field, props }) => {
            return {
                data: field.fetchInfo.response,
                countryCode: props.defaultCountryCode,
                isLoading: !field.fetchInfo.response
            };
        },
        extraProps: ({ t }) => {
            return {
                formatCreateLabel: formatCreateLabel(t)
            };
        },
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value?.value),
        fetchInfo: {
            fetcher: pincodesApi.fetch.bind(pincodesApi),
            params: ({ entity, props }) => {
                const countryCode = entity.getIn(['address', 'countryCode']);
                return { countryId: countryCode || props.defaultCountryCode };
            },
            response: null
        }
    },
    {
        accessor: 'address.countryCode',
        dependentAccessors: ['address.city', 'address.pincode'],
        label: 'homebaseForm.country',
        type: FieldTypeEnum.Select,
        options: CountryCodeOptions,
        placeholder: 'homebaseForm.countryPlaceholder',
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        },
        extraProps: ({ field, props }) => {
            return {
                isSearchable: true,
                value: field.value || props.defaultCountryCode
            };
        }
    },
    {
        accessor: 'latLng.lat',
        label: 'homebaseForm.lat',
        type: FieldTypeEnum.Text,
        placeholder: 'homebaseForm.latPlaceholder',
        halfWidth: true,
        required: true,
        onChangeHandler: ({ entity, value }) => {
            return entity.setLat(value);
        },
        extraProps: ({ entity }) => {
            const error = entity.getValidationMessage('lat');
            return {
                error
            };
        }
    },
    {
        accessor: 'latLng.lng',
        label: 'homebaseForm.lng',
        type: FieldTypeEnum.Text,
        placeholder: 'homebaseForm.lngPlaceholder',
        halfWidth: true,
        required: true,
        onChangeHandler: ({ entity, value }) => {
            return entity.setLng(value);
        },
        extraProps: ({ entity }) => {
            const error = entity.getValidationMessage('lng');
            return {
                error
            };
        }
    },
    {
        accessor: 'transactionDuration',
        label: 'homebaseForm.tat',
        helpText: 'homebaseForm.tatTooltip',
        type: FieldTypeEnum.TransactionDuration,
        halfWidth: true,
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value * 60)
    },
    {
        accessor: 'timeSlots',
        label: 'homebaseForm.slots',
        type: FieldTypeEnum.MultiDaySlots,
        helpText: 'homebaseForm.slotsTooltip',
        extraProps: ({ t }) => {
            return {
                translationsObject: {
                    add: t('homebaseForm.addSlots'),
                    change: t('homebaseForm.changeSlots')
                }
            };
        },
        getProcessedValueForField: ({ entity }) =>
            entity.initializeWeekSlots().timeSlots
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'homebaseSettings'
            };
        }
    }
];

const HOMEBASE_INITIAL_FETCH_FIELDS = ['address.city', 'address.pincode'];

const SingleHomebaseEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterHomebase,
    MasterActions: HomebaseActions,
    extraApiParams: {
        include: [IncludeTypeEnums.Contact]
    },
    EntityFetchAPI: homebaseApi,
    INITIAL_FETCH_FIELDS: HOMEBASE_INITIAL_FETCH_FIELDS,
    FIELDS: HOMEBASE_FIELDS,
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'homebaseId',
        ENTITY_SETTINGS_NAME: 'homebaseSettings'
    },
    mapEntityStateToProps: ({ state, props, baseProps }) => {
        const teams = teamsSelector(state, props);
        const defaultCountryCenter = ClientSelectors.getCountryCenterFromLocaleSettings(
            state,
            props
        );
        let defaultCountryCode = null;
        if (baseProps.localeSettings) {
            defaultCountryCode = baseProps.localeSettings.countryCode;
        }

        return {
            teams,
            defaultCountryCenter,
            defaultCountryCode
        };
    }
});

export default SingleHomebaseEditContainer;
